(() => {
  'use strict';
  let logosPromise;
  class BrandCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('.db-brands-track');
      this.names = JSON.parse(this.querySelector('[data-brand-names]').textContent);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.visible = false;
      this.direction = this.dataset.kind === 'beauty' ? -1 : 1;
      this.holdUntil = 0;
      this.maximum = 0;
      const on = (node, event, fn, options = {}) => node.addEventListener(event, fn, { ...options, signal });
      const hold = () => { this.holdUntil = performance.now() + 4500; };
      on(this, 'brands:change', event => { this.names = event.detail; if (this.logos) this.render(); });
      on(this.querySelector('[data-brand-prev]'), 'click', () => this.move(-1));
      on(this.querySelector('[data-brand-next]'), 'click', () => this.move(1));
      on(this.querySelector('[data-brand-pause]'), 'click', () => { this.paused = !this.paused; this.updateButton(); this.schedule(); });
      on(this.track, 'pointerenter', event => { if (event.pointerType === 'mouse') this.hovered = true; });
      on(this.track, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      on(this.track, 'focusin', () => { this.focused = true; });
      on(this.track, 'focusout', () => { this.focused = false; hold(); this.schedule(); });
      on(this.track, 'wheel', hold, { passive: true });
      on(this.track, 'pointerdown', event => {
        hold(); this.touching = true; this.dragged = false;
        if (event.pointerType === 'mouse' && event.button === 0) this.drag = { x: event.clientX, scroll: this.track.scrollLeft, id: event.pointerId };
      });
      on(this.track, 'pointermove', event => {
        if (!this.drag) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 5) {
          this.dragged = true;
          this.track.dataset.dragging = 'true';
          this.track.setPointerCapture(event.pointerId);
          this.track.scrollLeft = this.drag.scroll - distance;
          this.direction = distance > 0 ? -1 : 1;
        }
      });
      const release = () => {
        if (!this.touching && !this.drag) return;
        this.drag = null; this.touching = false; this.track.dataset.dragging = 'false'; hold(); this.schedule();
      };
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(this.track, 'dragstart', event => event.preventDefault());
      on(this.track, 'click', event => { if (this.dragged) { event.preventDefault(); this.dragged = false; } });
      on(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.motion, 'change', () => { this.paused = this.motion.matches; this.updateButton(); this.schedule(); });
      this.resize = new ResizeObserver(() => {
        this.maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        this.querySelector('.db-brands-controls').hidden = this.maximum < 2;
        this.schedule();
      });
      this.resize.observe(this.track);
      this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.intersection.observe(this);
      this.updateButton();
      logosPromise ||= fetch(this.dataset.logos).then(r => { if (!r.ok) throw new Error('Logos unavailable'); return r.json(); }).catch(() => ({}));
      logosPromise.then(logos => { if (signal.aborted) return; this.logos = logos; this.render(); });
    }
    render() {
      const brands = [...new Map(this.names.filter(name => name && !/^(shiingo|shingo)$/i.test(name.trim())).map(name => [name.trim().toLowerCase(), name])).values()];
      this.hidden = brands.length === 0;
      this.track.replaceChildren(...brands.map(brand => {
        const link = document.createElement('a');
        link.className = 'db-brand'; link.dataset.brand = brand; link.setAttribute('aria-label', brand);
        const url = new URL(this.dataset.url, location.origin);
        if (this.dataset.kind === 'beauty') url.pathname = url.pathname.replace(/\/collections\/[^/]+$/, '/collections/vendors');
        url.searchParams.set(this.dataset.kind === 'footwear' ? 'brand' : 'q', brand);
        url.searchParams.set('filter.v.availability', '1'); link.href = url.pathname + url.search;
        const logo = this.logos[brand.trim().toLowerCase()];
        if (logo) {
          const image = document.createElement('img');
          image.src = logo.url || this.dataset.assetBase + logo.file + (logo.version ? `?v=${encodeURIComponent(logo.version)}` : '');
          image.alt = brand; image.width = 150; image.height = 70;
          image.loading = 'lazy'; image.decoding = 'async'; image.draggable = false;
          if (logo.zoom) image.style.transform = `scale(${logo.zoom})`;
          if (logo.invert) image.dataset.invert = 'true';
          if (logo.fit) image.dataset.fit = logo.fit;
          image.addEventListener('error', () => { image.replaceWith(document.createTextNode(brand)); }, { once: true });
          link.append(image);
        } else link.textContent = brand;
        return link;
      }));
      this.maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollLeft = this.direction < 0 ? this.maximum : 0;
      this.dataset.ready = 'true'; this.schedule();
    }
    updateButton() {
      const button = this.querySelector('[data-brand-pause]');
      button.setAttribute('aria-pressed', String(this.paused));
      button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
      button.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
    }
    move(direction) {
      this.direction = direction; this.holdUntil = performance.now() + 4500;
      this.track.scrollBy({ left: direction * Math.max(180, this.track.clientWidth * .65), behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.schedule();
    }
    schedule() {
      cancelAnimationFrame(this.frame); this.lastTime = null; this.position = this.track.scrollLeft;
      if (!this.isConnected || !this.visible || document.hidden || this.paused || this.maximum < 2) return;
      const tick = now => {
        const elapsed = this.lastTime ? Math.min(now - this.lastTime, 50) : 0; this.lastTime = now;
        if (!this.hovered && !this.focused && !this.touching && now > this.holdUntil) {
          const next = this.position + this.direction * elapsed * .035;
          if (next >= this.maximum) this.direction = -1;
          if (next <= 0) this.direction = 1;
          this.position = Math.max(0, Math.min(this.maximum, next));
          this.track.scrollLeft = this.position;
        } else this.position = this.track.scrollLeft;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); this.intersection?.disconnect(); cancelAnimationFrame(this.frame);
    }
  }
  if (!customElements.get('db-brand-carousel')) customElements.define('db-brand-carousel', BrandCarousel);
})();
